import React, { useEffect, useState } from "react";
import axios from "axios";
import endPoint from "../../websitecomponents/redux/EndPoint/endpoint";
import { toast } from "react-toastify";

const ResellerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Filters
  const [searchOrderId, setSearchOrderId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // ================= FETCH ALL ORDERS =================
  const fetchOrders = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("ResellerTokenData");

      const res = await axios.get(endPoint.ResellerOrders, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.success) {
        setOrders(res.data.orders || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch reseller orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ================= SEARCH BY ORDER ID / STATUS =================
  const searchOrders = async () => {
    try {
      const token = localStorage.getItem("ResellerTokenData");

      if (!searchOrderId && !selectedStatus) {
        fetchOrders();
        return;
      }

      setLoading(true);

      let query = [];

      if (searchOrderId.trim()) {
        query.push(`orderID=${searchOrderId}`);
      }

      if (selectedStatus) {
        query.push(`status=${selectedStatus}`);
      }

      const url = `${endPoint.ResellerOrderByIdorStatus}?${query.join("&")}`;

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.success) {
        setOrders(res.data.orders || []);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error(error);
      setOrders([]);
      toast.error("No matching reseller orders found");
    } finally {
      setLoading(false);
    }
  };

  // ================= UPDATE STATUS =================
  const updateStatus = async (orderID, newStatus) => {
    try {
      const token = localStorage.getItem("ResellerTokenData");

      const res = await axios.put(
        `${endPoint.ResellerUpdateOrderStatus}/${orderID}`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data?.success) {
        toast.success("Order status updated successfully");
        searchOrders();
      } else {
        toast.error("Status update failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update order status");
    }
  };

  // ================= CLEAR FILTERS =================
  const handleClear = () => {
    setSearchOrderId("");
    setSelectedStatus("");
    fetchOrders();
  };

  return (
    <div className="p-4 md:p-6">
      {/* ================= TOP FILTER SECTION ================= */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">

        <div className="flex flex-wrap gap-3 items-end">
          {/* Order ID Search */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Order ID</label>
            <input
              type="text"
              placeholder="Search by Order ID"
              value={searchOrderId}
              onChange={(e) => setSearchOrderId(e.target.value)}
              className="w-64 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
            />
          </div>

          {/* Status Dropdown */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-52 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="received">Received</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Search Button */}
          <button
            onClick={searchOrders}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Search
          </button>

          {/* Refresh Button */}
          <button
            onClick={handleClear}
            className="px-5 py-2 rounded-lg border bg-gray-100 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="bg-white border shadow rounded-lg">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-20 flex justify-center">
              <div className="h-12 w-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="py-20 text-center">
              <h3 className="text-lg font-medium text-gray-700">
                No Reseller Orders Found
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Reseller orders will appear here
              </p>
            </div>
          ) : (
            <table className="min-w-full w-max border-collapse text-sm md:text-base">
              <thead className="bg-gray-100">
                <tr>
                  {[
                    "Order ID",
                    "Customer ID",
                    "Customer Email",
                    "Items",
                    "Seller Code",
                    "Seller Email",
                    "Payment Mode",
                    "Payment Status",
                    "Shipping Cost",
                    "Total Price",
                    "Total Earning",
                    "Shipping Address",
                    "Created At",
                    "Status",
                  ].map((header) => (
                    <th
                      key={header}
                      className="border px-3 py-2 text-left whitespace-nowrap"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {orders.map((order) => {
                  const totalEarning = order.items.reduce(
                    (sum, item) => sum + Number(item.resellerEarning || 0),
                    0,
                  );

                  return (
                    <tr
                      key={order.orderID}
                      className="hover:bg-gray-50 align-top"
                    >
                      <td className="border px-3 py-2 font-semibold">
                        {order.orderID}
                      </td>

                      <td className="border px-3 py-2">
                        {order.customer?._id || "N/A"}
                      </td>

                      <td className="border px-3 py-2">
                        {order.address?.email || "-"}
                      </td>

                      {/* Items */}
                      <td className="border px-3 py-2">
                        <table className="w-full border text-xs md:text-sm">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="border px-2 py-1">Image</th>
                              <th className="border px-2 py-1">Product</th>
                              <th className="border px-2 py-1">Qty</th>
                              <th className="border px-2 py-1">Price</th>
                            </tr>
                          </thead>

                          <tbody>
                            {order?.items?.map((item, i) => (
                              <tr key={i}>
                                <td className="border px-2 py-1">
                                  {item.productImage ? (
                                    <img
                                      src={item.productImage}
                                      alt={item.title}
                                      className="w-12 h-12 object-cover rounded"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 flex items-center justify-center text-xs text-gray-400 bg-gray-100 rounded">
                                      No Image
                                    </div>
                                  )}
                                </td>

                                <td className="border px-2 py-1">
                                  {item.title || "-"}
                                </td>

                                <td className="border px-2 py-1 text-center">
                                  {item.quantity}
                                </td>

                                <td className="border px-2 py-1">
                                  ₹{item.totalPrice}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>

                      <td className="border px-3 py-2">
                        {order.items?.[0]?.sellerCode || "-"}
                      </td>

                      <td className="border px-3 py-2">
                        {order.items?.[0]?.sellerEmail || "-"}
                      </td>

                      <td className="border px-3 py-2">
                        {order.paymentMode || "-"}
                      </td>

                      <td className="border px-3 py-2">
                        {order.paymentStatus || "-"}
                      </td>

                      <td className="border px-3 py-2">
                        ₹{order.shippingCost || 0}
                      </td>

                      <td className="border px-3 py-2 font-semibold text-blue-600">
                        ₹{order.resellerTotal || 0}
                      </td>

                      <td className="border px-3 py-2 font-bold text-green-600">
                        ₹{totalEarning}
                      </td>

                      <td className="border px-3 py-2">
                        {order.address?.city || "-"}
                      </td>

                      <td className="border px-3 py-2">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>

                      <td className="border px-3 py-2">
                        <select
                          value={order.status}
                          onChange={(e) =>
                            updateStatus(order.orderID, e.target.value)
                          }
                          className="px-3 py-2 rounded border text-sm font-medium"
                        >
                          <option value="received">Received</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResellerOrders;